import { CWElementsConfig, useConfig } from '@cw-elements/config';
import { CROPWISE_MFES_PATH } from '@cw-elements/mfe-dynamic-slot';
import { createBrowserRouter } from '@datadog/browser-rum-react/react-router-v7';
import { Suspense, lazy } from 'react';
import { RouterProvider } from 'react-router-dom';
import { createAppGuideIntegration } from 'src/analytics/guides/appGuides';
import { initObservability } from 'src/analytics/initObservability';
import { SuspenseLoading } from '../components/shared/SuspenseLoading';
import AuthRedirect from './Authentication/AuthRedirect';
import Authentication from './Authentication/Authentication';
import DashboardLoading from './Dashboard/DashboardLoading';
import { PropertyEntry } from './Properties/PropertyEntry';
import { RootEmpty } from './RootEmpty';
import { RootLayout } from './RootLayout';
import { ViewNotFoundRedirect } from './ViewNotFoundRedirect';
import { ModulesContainer } from './modules/modules.container';
import { SubscriptionsModule } from './modules/subscriptions.module';
import { routePaths } from './routes';

const Organizations = lazy(() => import('./Organizations/index'));
const Dashboard = lazy(() => import('./Dashboard'));
const Analytics = lazy(() => import('./Analytics'));
const Prescriptions = lazy(() => import('./Prescriptions/Prescriptions'));
const SubscriptionManagement = lazy(
  () => import('../components/SubscriptionManagement/SubscriptionManagement')
);
const PrescriptionsCreate = lazy(
  () => import('./Prescriptions/PrescriptionCreate/PrescriptionCreate')
);
const PrescriptionsEdit = lazy(
  () => import('./Prescriptions/PrescriptionEdit/PrescriptionEditWrapper')
);
const MfeDynamicSlot = lazy(() => import('@cw-elements/mfe-dynamic-slot'));
const YieldAnalysisDrawer = lazy(() => import('./YieldAnalysis/YieldAnalysisDrawer'));
const ProductivityMaps = lazy(() => import('./ProductivityMaps'));
const ProductivityMapsCreate = lazy(
  () => import('./ProductivityMaps/Create/CreateProductivityMap')
);
const NotesDrawer = lazy(() => import('./Map/Notes/NotesDrawer'));
const NotesCreate = lazy(() => import('./Map/Notes/CreateNote'));
const NotesEdit = lazy(() => import('./Map/Notes/EditNote'));
const MapView = lazy(() => import('./Map'));
const DataLayers = lazy(() => import('./DataLayers'));
const AnomalyDetectionComparison = lazy(
  () =>
    import(
      './Map/components/AnomalyDetection/AnomalyDetectionComparison/AnomalyDetectionComparison'
    )
);
const AiInsightsDrawer = lazy(
  () => import('./Map/components/Overlays/AiInsights/AiInsightsDrawer')
);

const router = createBrowserRouter([
  {
    path: routePaths.root,
    element: <Authentication />,
    errorElement: <RootEmpty />,
  },
  {
    path: routePaths.redirectAuth,
    element: <AuthRedirect />,
  },
  {
    path: routePaths.signIn,
    element: <Authentication />,
  },
  {
    path: routePaths.app,
    element: <RootLayout />,
    children: [
      {
        path: routePaths.workspaceDashboard,
        element: (
          <Suspense key={routePaths.workspaceDashboard} fallback={<DashboardLoading />}>
            <Dashboard />
          </Suspense>
        ),
      },
      {
        path: `${routePaths.organizations}/*`,
        element: (
          <Suspense fallback={<SuspenseLoading />}>
            <Organizations />
          </Suspense>
        ),
      },
      {
        path: CROPWISE_MFES_PATH,
        element: (
          <Suspense fallback={<SuspenseLoading />}>
            <MfeDynamicSlot
              getConfigInstance={useConfig as () => CWElementsConfig}
              onCancel={() => {
                router.navigate(-1);
              }}
            />
          </Suspense>
        ),
      },
      {
        path: routePaths.subscriptions,
        element: (
          <Suspense fallback={<SuspenseLoading />}>
            <SubscriptionManagement />
          </Suspense>
        ),
      },
      {
        path: routePaths.properties,
        element: (
          <Suspense fallback={<SuspenseLoading />}>
            <PropertyEntry />
          </Suspense>
        ),
        children: [
          {
            path: routePaths.view,
            element: (
              <Suspense fallback={<SuspenseLoading />}>
                <MapView />
              </Suspense>
            ),
            children: [
              {
                path: routePaths.aiInsights,
                element: (
                  <Suspense fallback={<SuspenseLoading darkMode />}>
                    <AiInsightsDrawer />
                  </Suspense>
                ),
              },
              {
                path: routePaths.notes,
                element: (
                  <Suspense fallback={<SuspenseLoading darkMode />}>
                    <NotesDrawer />
                  </Suspense>
                ),
              },
              {
                path: routePaths.notesCreate,
                element: (
                  <Suspense fallback={<SuspenseLoading darkMode />}>
                    <NotesCreate />
                  </Suspense>
                ),
              },
              {
                path: routePaths.notesEdit,
                element: (
                  <Suspense fallback={<SuspenseLoading darkMode />}>
                    <NotesEdit />
                  </Suspense>
                ),
              },
              {
                path: routePaths.prescriptions,
                element: (
                  <Suspense fallback={<SuspenseLoading darkMode />}>
                    <Prescriptions />
                  </Suspense>
                ),
              },
              {
                path: routePaths.prescriptionsCreate,
                element: (
                  <Suspense fallback={<SuspenseLoading darkMode />}>
                    <PrescriptionsCreate />
                  </Suspense>
                ),
              },
              {
                path: routePaths.prescriptionsEdit,
                element: (
                  <Suspense fallback={<SuspenseLoading darkMode />}>
                    <PrescriptionsEdit />
                  </Suspense>
                ),
              },
              {
                path: routePaths.anomalyComparison,
                element: (
                  <Suspense fallback={<SuspenseLoading darkMode />}>
                    <AnomalyDetectionComparison />
                  </Suspense>
                ),
              },
              {
                path: routePaths.yieldAnalysis,
                element: (
                  <Suspense fallback={<SuspenseLoading darkMode />}>
                    <YieldAnalysisDrawer />
                  </Suspense>
                ),
              },
            ],
          },
          {
            path: routePaths.viewNotFound,
            element: <ViewNotFoundRedirect />,
          },
          {
            path: routePaths.analytics,
            element: (
              <Suspense fallback={<SuspenseLoading />}>
                <Analytics />
              </Suspense>
            ),
          },
          {
            path: routePaths.productivityMaps,
            element: (
              <Suspense fallback={<SuspenseLoading />}>
                <ProductivityMaps />
              </Suspense>
            ),
            children: [
              {
                path: routePaths.productivityMapsCreate,
                element: (
                  <Suspense fallback={<SuspenseLoading />}>
                    <ProductivityMapsCreate />
                  </Suspense>
                ),
              },
            ],
          },
          {
            path: routePaths.dashboard,
            element: (
              <Suspense key={routePaths.dashboard} fallback={<DashboardLoading />}>
                <Dashboard />
              </Suspense>
            ),
          },
          {
            path: routePaths.dataLayers,
            element: (
              <Suspense fallback={<SuspenseLoading />}>
                <DataLayers />
              </Suspense>
            ),
          },
        ],
      },
    ],
  },
  {
    path: routePaths.modules,
    element: <ModulesContainer />,
    children: [
      {
        path: routePaths.subscriptionsModule,
        element: <SubscriptionsModule />,
      },
    ],
  },
]);

void initObservability(createAppGuideIntegration(router));

export function Router() {
  return <RouterProvider router={router} />;
}
